/**
 * Field/section visibility for manifest config — `displayOptions.showIf` / `hideIf`.
 *
 * - showIf: ANY clause satisfied (OR-of-clauses, each clause AND-of-keys) → show
 * - hideIf: ANY clause satisfied (OR-of-clauses, each clause OR-of-keys) → hide
 *
 * The underlying condition matching (value-equality + `whenSet`/`whenUnset`, ref resolution
 * against form values or `company`) lives in `condition-match.ts` — this module only maps the
 * show/hide polarity onto it. Both the React `useDisplayOptions` hook (render-time) and the Zod
 * `buildFormSchema` superRefine (validation-time) call `shouldShow` for identical semantics.
 */
import type { CompanyInfo, ConfigField } from '@scio3/shared-types';
import { toClauseArray, evaluateShowIfClause, evaluateHideIfClause } from './condition-match';

/** Anything with optional displayOptions (fields and sections) */
export type HasDisplayOptions = Pick<ConfigField, 'displayOptions'>;

/** Names reserved inside `showIf` / `hideIf` for set/unset checks. Anything else in the map is
 *  treated as a field-ref → value-array entry. Consumed by the validator
 *  (`checkDisplayConditionRefs`) so it stays in sync with `condition-match`'s `splitCondition`. */
export const RESERVED_DISPLAY_CONDITION_KEYS = ['whenSet', 'whenUnset'] as const;

// Re-export so existing importers (e.g. validate-manifest) keep a single import site.
export { toClauseArray } from './condition-match';

/** Check if an element (field or section) should be visible given current form values.
 *  Both `showIf` and `hideIf` accept either a single `DisplayCondition` or an array.
 *  An array means OR-of-clauses: any clause matching its own AND/OR semantics fires. */
export function shouldShow(
  element: HasDisplayOptions,
  formValues: Record<string, unknown>,
  company?: CompanyInfo | null,
): boolean {
  if (element.displayOptions?.showIf) {
    // OR across clauses — any AND-satisfied clause shows the field.
    return toClauseArray(element.displayOptions.showIf).some((c) =>
      evaluateShowIfClause(c, formValues, company),
    );
  }

  if (element.displayOptions?.hideIf) {
    // OR across clauses — any OR-satisfied clause hides the field.
    return !toClauseArray(element.displayOptions.hideIf).some((c) =>
      evaluateHideIfClause(c, formValues, company),
    );
  }

  return true;
}
